import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageBanner from '../../components/PageBanner';
import { FaFacebookF, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import { getBlogById, getBlogs } from '../../services/data.service';

const API_BASE = process.env.REACT_APP_DATABASEURL || '';

// Normalize a backend blog doc to the shape this page renders (uses `id`).
const normalize = (b) => (b ? { ...b, id: b.blogId || b.id } : b);

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;

    (async () => {
      // Load the post + list (for related) from the backend.
      try {
        const [postRes, listRes] = await Promise.all([
          getBlogById(id),
          getBlogs(),
        ]);
        const found = normalize(postRes?.data);
        const list = Array.isArray(listRes?.data) ? listRes.data.map(normalize) : [];
        if (!active) return;
        if (found) {
          setBlog(found);
          setRelatedBlogs(list.filter((b) => b.id !== found.id).slice(0, 3));
        } else {
          navigate('/blog');
        }
      } catch (e) {
        if (active) navigate('/blog');
      }
    })();

    return () => { active = false; };
  }, [id, navigate]);

  if (!blog) return null;

  const shareUrl = encodeURIComponent(window.location.href);
  const shareText = encodeURIComponent(blog.title);

  return (
    <>
      <Header />

      <div className="blog-details-root">
        <PageBanner
          title={blog.title}
          eyebrow={blog.category}
          subtitle={blog.excerpt}
        >
          <div className="bd-hero-actions" style={{ marginTop: "2rem" }}>
            <Link to="/blog" className="back-link">All Articles</Link>
            <div className="share-strip">
              <a className="share-btn" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noreferrer" aria-label="Share on Facebook">
                <FaFacebookF />
              </a>
              <a className="share-btn" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`} target="_blank" rel="noreferrer" aria-label="Share on Twitter">
                <FaTwitter />
              </a>
              <a className="share-btn" href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noreferrer" aria-label="Share on WhatsApp">
                <FaWhatsapp />
              </a>
            </div>
          </div>
        </PageBanner>

        <section className="bd-reading-zone">
          <div className="container">
            <div className="bd-layout">
              <aside className="bd-sticky">
                <div className="author-card">
                  <div className="avatar">{blog.author.charAt(0)}</div>
                  <span className="label">About the Author</span>
                  <h3>{blog.author}</h3>
                  <p>Academic expert and contributor at NCET, sharing practical insights on education, placements, technology, and campus development.</p>
                </div>

                <div className="quick-links">
                  <h4>Explore More</h4>
                  <Link to="/blog">All Blogs</Link>
                  <Link to="/placements">Placements</Link>
                  <Link to="/contact">Contact NCET</Link>
                </div>
              </aside>

              <article className="bd-article-card">
                <div className="blog-prose" dangerouslySetInnerHTML={{ __html: blog.content }} />
              </article>
            </div>
          </div>
        </section>

        <section className="bd-promo">
          <div className="container">
            <div className="promo-panel">
              <h2>Ready to Shape Your Engineering Future?</h2>
              <p>Join Nagarjuna College of Engineering and Technology and experience world-class education with an industry-aligned curriculum and student-first ecosystem.</p>
              <div className="promo-actions">
                <Link to="/admission-process" className="btn-apply">Apply Now</Link>
                <Link to="/contact" className="btn-contact">Contact Us</Link>
              </div>
            </div>
          </div>
        </section>

        {relatedBlogs.length > 0 && (
          <section className="bd-related">
            <div className="container">
              <div className="bd-related-head">
                <span className="bd-related-head__eyebrow">You Might Also Like</span>
                <h2>Related Stories</h2>
                <p>More insights from NCET Pulse on academics, placements, and campus life.</p>
              </div>
              <div className="bd-related-grid">
                {relatedBlogs.map((rb) => (
                  <Link key={rb.id} to={`/blog/${rb.id}`} className="bd-related-card">
                    <span className="bd-related-card__category">{rb.category}</span>
                    <h3>{rb.title}</h3>
                    <p>{rb.excerpt}</p>
                    <div className="bd-related-card__meta">
                      <time>{rb.date}</time>
                      <span>{rb.readTime}</span>
                    </div>
                    <span className="bd-related-card__link">
                      Continue reading
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <Footer />
    </>
  );
};

export default BlogDetails;
